const formatOrderDate = (order) => {
  const value =
    order?.createDate ??
    order?.createdAt ??
    order?.date ??
    order?.charge?.created;

  if (value == null) return "—";

  let date;
  if (typeof value === "number") {
    date = new Date(value < 1e12 ? value * 1000 : value);
  } else {
    date = new Date(value);
  }

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatCad = (amount, currency = "CAD") => {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  return `${currency || "CAD"} $${Number(amount).toFixed(2)}`;
};

const getOrderTotal = (order) => {
  if (order?.checkout?.total != null) return Number(order.checkout.total);
  if (order?.total != null) return Number(order.total);

  const amount = order?.amount ?? order?.charge?.amount;
  if (amount == null) return null;

  const numericAmount = Number(amount);
  if (Number.isNaN(numericAmount)) return null;

  return numericAmount > 100 ? numericAmount / 100 : numericAmount;
};

const getOrderItems = (order) => {
  if (Array.isArray(order?.checkout?.items)) return order.checkout.items;
  if (Array.isArray(order?.items)) return order.items;
  if (Array.isArray(order?.lineItems)) return order.lineItems;
  return [];
};

const getItemQuantity = (item) => {
  if (!Array.isArray(item?.sizes) || item.sizes.length === 0) return 1;
  return item.sizes.reduce((sum, size) => sum + (size.quantity ?? 0), 0);
};

const getTotalItemCount = (items) =>
  items.reduce((sum, item) => sum + getItemQuantity(item), 0);

const getOrderId = (order) =>
  order?._id ?? order?.orderId ?? order?.id ?? null;

const getOrderNumber = (order) => {
  if (order?.orderNumber != null && order.orderNumber !== "") {
    return String(order.orderNumber);
  }
  return null;
};

const getPaymentStatus = (order) => {
  if (order?.charge?.status) return order.charge.status;
  if (order?.charge?.paid) return "paid";
  return "—";
};

const getOrderStatus = (order) => order?.status ?? "processing";

const getCardDisplay = (order) => {
  const card =
    order?.charge?.payment_method_details?.card ?? order?.charge?.source;

  if (!card?.last4) return null;

  const brand = (card.brand || card.network || "Card").toString();
  const label = brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();

  return `${label} •••• ${card.last4}`;
};

const formatShippingMethod = (method) => {
  if (!method) return "—";
  return method
    .split(/[-_]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatShippingAddress = (address) => {
  if (!address) return null;

  const name = [address.firstName, address.lastName].filter(Boolean).join(" ");
  const company = address.company?.trim();
  const street = [address.addressLineOne, address.addressLineTwo]
    .filter(Boolean)
    .join(", ");
  const cityLine = [
    address.city,
    address.stateProvince,
    address.zipPostal,
    address.country,
  ]
    .filter(Boolean)
    .join(", ");

  return {
    name,
    company,
    street,
    cityLine,
    phone: address.phone?.trim() || null,
    email: address.email?.trim() || null,
  };
};

const getArtworkOptionLabels = (item) => {
  const labels = [];
  if (item?.productType === "gang-sheet") labels.push("Gang Sheet");
  if (item?.removeBackground) labels.push("Remove Background");
  if (item?.cutSeparately) labels.push("Cut Separately");
  if (item?.artworkCheck) labels.push("Artwork Check");
  return labels;
};

const getSizeLabel = (item) => {
  const primarySize = item?.sizes?.[0];
  if (!primarySize) return null;
  return `${primarySize.width}" × ${primarySize.height}"`;
};

const getReceiptUrl = (order) => order?.charge?.receipt_url ?? null;

const getArtworkUrl = (item) =>
  item?.secureUrl ??
  item?.previewUrl ??
  item?.secure_url ??
  item?.preview_url ??
  null;

const getArtworkFileName = (item, orderNumber) => {
  const baseName = item?.fileName ?? item?.file_name ?? "artwork";
  const nameWithoutExt = baseName.includes(".")
    ? baseName.slice(0, baseName.lastIndexOf("."))
    : baseName;

  const primarySize = item?.sizes?.[0];
  const width = primarySize?.width ?? item?.width ?? 0;
  const height = primarySize?.height ?? item?.height ?? 0;

  let extension = "";
  if (baseName.includes(".")) {
    extension = baseName.slice(baseName.lastIndexOf("."));
  } else {
    const url = getArtworkUrl(item);
    if (url) {
      const urlPath = url.split("?")[0];
      const urlExtension = urlPath.split(".").pop();
      if (urlExtension && urlExtension.length <= 5) {
        extension = `.${urlExtension}`;
      }
    }
    if (!extension) extension = ".png";
  }

  const sanitize = (value) =>
    String(value ?? "")
      .trim()
      .replace(/[/\\?%*:|"<>]/g, "_");

  const orderPart = sanitize(orderNumber ?? "order");
  const filePart = sanitize(nameWithoutExt);

  return `${orderPart}_${width}X${height}_${filePart}${extension}`;
};

const getArtworkDownloadUrl = (item, fileName) => {
  const url = getArtworkUrl(item);
  if (!url) return null;

  if (url.includes("/upload/")) {
    const [prefix, suffix] = url.split("/upload/");
    const safeName = (fileName || "artwork").replace(/,/g, "_");
    return `${prefix}/upload/fl_attachment:${safeName}/${suffix}`;
  }

  return url;
};

const downloadArtwork = async (item, orderNumber) => {
  const url = getArtworkUrl(item);
  if (!url) return;

  const fileName = getArtworkFileName(item, orderNumber);

  const triggerBlobDownload = async (sourceUrl) => {
    const response = await fetch(sourceUrl);
    if (!response.ok) throw new Error("Download failed");

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  };

  try {
    await triggerBlobDownload(url);
  } catch {
    try {
      await triggerBlobDownload(getArtworkDownloadUrl(item, fileName));
    } catch {
      window.open(getArtworkDownloadUrl(item, fileName), "_blank", "noopener,noreferrer");
    }
  }
};

export {
  formatOrderDate,
  formatCad,
  getOrderTotal,
  getOrderItems,
  getItemQuantity,
  getTotalItemCount,
  getOrderId,
  getOrderNumber,
  getOrderStatus,
  getPaymentStatus,
  getCardDisplay,
  formatShippingMethod,
  formatShippingAddress,
  getSizeLabel,
  getArtworkOptionLabels,
  getReceiptUrl,
  getArtworkUrl,
  getArtworkFileName,
  getArtworkDownloadUrl,
  downloadArtwork,
};
